<template>
  <h2 class="mb-4">
    {{ t('meterReadings') }}
  </h2>
  <div class="mb-6 grid gap-4 md:grid-cols-6">
    <VehicleStatus
      v-if="booking"
      v-model:milage="formValues.milage"
      v-model:fuel-level="formValues.fuelLevel"
      class="md:col-span-4"
      :disabled="isSaving"
      :fuel-type="fuelType"
      :show-milage-warning="!!formValues.milage && formValues.milage < allocation.car.milage"
      @fuel-level-touched="fuelLevelChanged = true"
    />

    <LabeledText v-if="allocation.milageStart" class="md:col-span-2" :label="t('milageStart')">
      <p class="flex h-8 items-center">
        {{ `${allocation.milageStart} km` }}
      </p>
    </LabeledText>
  </div>
  <div class="grid md:flex md:justify-end">
    <CVButton
      v-if="isSaving || milageChanged || fuelLevelChanged"
      :is-loading="isSaving"
      variant="default"
      class="max-sm:w-full"
      @click="handleSave"
      >{{ t('save') }}</CVButton
    >
  </div>
  <CleaningFeesSummary :booking="booking" />
  <AddCleaningFeeModal
    :booking-id="booking.id"
    :customer-locale="booking.displayLocale ?? Local.EN_US"
  />
</template>

<script setup lang="ts">
import VehicleStatus from '@/components/booking-forms/VehicleStatus.vue';
import LabeledText from '@/components/LabeledText.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { computed, reactive, ref } from 'vue';
import { cloneDeep } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import AddCleaningFeeModal from './AddCleaningFeeModal.vue';
import CleaningFeesSummary from '@/views/handover/components/return/return-car-status/components/CleaningFeesSummary.vue';
import { Local } from '@carvia/ros-client-types';

const props = defineProps<{
  booking: Booking;
  allocation: Allocation;
  isSaving: boolean;
}>();

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'save', values: { milage: number; fuelLevel: number }): void;
}>();

const fuelType = computed(
  () => props.allocation.car.fuelType ?? props.allocation.car.vehicleType.fuelType,
);

const formValues = reactive({
  milage: props.allocation.milageEnd,
  fuelLevel:
    props.allocation.fuelLevelEnd ??
    props.allocation.fuelLevelStart ??
    props.allocation.car.fuelLevel ??
    0,
});

const milageChanged = computed(() => {
  return props.allocation.milageEnd !== formValues.milage;
});
const fuelLevelChanged = ref(false);

const unchangedValues = ref(cloneDeep(formValues));

const handleSave = async () => {
  if (!formValues.milage) return;
  emit('save', {
    milage: formValues.milage,
    fuelLevel: formValues.fuelLevel,
  });
  fuelLevelChanged.value = false;
  unchangedValues.value = cloneDeep(formValues);
};

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "back": "back to damages",
    "milesDriven": "Miles Driven",
    "enterMilage": "Please enter the meter milage",
    "summary": "Summary",
    "milageStart": "Milage at rental start",
    "meterReadings": "Meter Readings"
  },
  "de": {
    "back": "Zurück zu den Schäden",
    "milesDriven": "Gefahrene Kilometer",
    "enterMilage": "Bitte geben Sie den Kilometerstand an",
    "summary": "Zusammenfassung",
    "milesIncluded": "Inklusive Kilometer",
    "milageStart": "Kilometerstand bei Mietbeginn",
    "meterReadings": "Zählerstände"
  },
  "es": {
    "back": "volver a daños",
    "milesDriven": "Kilómetros recorridos",
    "enterMilage": "Introduzca el kilometraje del cuentakilómetros",
    "summary": "Resumen",
    "milageStart": "Kilometraje al inicio del alquiler",
    "meterReadings": "Lecturas del cuentakilómetros"
  }
}
</i18n>
